import { useState } from "react";
import { putContributionCategoriesForTenant } from "../../../../api/contributionCategories";
import { reportError } from "../../../../api/errors";
import { useAdminData } from "../../AdminDataContext";
import { contributionStore, type ContributionCategory, type PointsSubjectType } from "../../utils/contributionStore";

const unitOptions = ["小时", "场次", "次", "篇", "人", "天", "项", "个"];

interface FormState {
  name: string;
  subjectType: PointsSubjectType;
  unit: string;
  customUnit: string;
  pointsPerUnit: number | "";
  description: string;
  isActive: boolean;
}

const emptyForm: FormState = {
  name: "",
  subjectType: "award",
  unit: "小时",
  customUnit: "",
  pointsPerUnit: "",
  description: "",
  isActive: true,
};

export default function ContributionConfig() {
  const { tenantLegacyId, reload } = useAdminData();
  const [categories, setCategories] = useState<ContributionCategory[]>(() => contributionStore.getAll());
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [searchText, setSearchText] = useState("");

  const activeCount = categories.filter((c) => c.isActive).length;
  const totalPoints = categories.filter((c) => c.isActive).reduce((sum, c) => sum + c.pointsPerUnit, 0);

  const filtered = categories.filter(
    (c) => c.name.includes(searchText) || c.unit.includes(searchText) || c.description.includes(searchText)
  );

  const persistContributions = async (): Promise<boolean> => {
    try {
      const next = await putContributionCategoriesForTenant(tenantLegacyId, contributionStore.getAll());
      contributionStore.replaceFromApi(next);
      setCategories(contributionStore.getAll());
      void reload({ silent: true });
      return true;
    } catch (e) {
      reportError(e);
      void reload({ silent: true });
      return false;
    }
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (cat: ContributionCategory) => {
    const isCustomUnit = !unitOptions.includes(cat.unit);
    setEditingId(cat.id);
    setForm({
      name: cat.name,
      subjectType: cat.subjectType ?? "award",
      unit: isCustomUnit ? "custom" : cat.unit,
      customUnit: isCustomUnit ? cat.unit : "",
      pointsPerUnit: cat.pointsPerUnit,
      description: cat.description,
      isActive: cat.isActive,
    });
    setShowModal(true);
  };

  const handleSave = () => {
    void (async () => {
      const finalUnit = form.unit === "custom" ? form.customUnit.trim() : form.unit;
      if (!form.name.trim() || !finalUnit || form.pointsPerUnit === "" || Number(form.pointsPerUnit) <= 0) return;

      if (editingId) {
        const changes = {
          name: form.name.trim(), unit: finalUnit,
          pointsPerUnit: Number(form.pointsPerUnit),
          description: form.description.trim(), isActive: form.isActive,
          subjectType: form.subjectType,
        };
        contributionStore.update(editingId, changes);
        setCategories(contributionStore.getAll());
      } else {
        const newCat: ContributionCategory = {
          id: `c${Date.now()}`,
          name: form.name.trim(), unit: finalUnit,
          pointsPerUnit: Number(form.pointsPerUnit),
          description: form.description.trim(), isActive: form.isActive,
          subjectType: form.subjectType,
          createdAt: new Date().toISOString().slice(0, 10),
        };
        contributionStore.add(newCat);
        setCategories(contributionStore.getAll());
      }

      if (await persistContributions()) {
        setShowModal(false);
        setSavedFlash(true);
        setTimeout(() => setSavedFlash(false), 2000);
      }
    })();
  };

  const handleDelete = (id: string) => {
    void (async () => {
      contributionStore.remove(id);
      setCategories(contributionStore.getAll());
      setDeleteConfirmId(null);
      await persistContributions();
    })();
  };

  const toggleActive = (id: string) => {
    const cat = categories.find((c) => c.id === id);
    if (cat) contributionStore.update(id, { isActive: !cat.isActive });
    setCategories(contributionStore.getAll());
    void persistContributions();
  };

  const isFormValid =
    form.name.trim() &&
    (form.unit !== "custom" ? form.unit : form.customUnit.trim()) &&
    form.pointsPerUnit !== "" &&
    Number(form.pointsPerUnit) > 0;

  return (
    <div>
      {/* Stats Bar */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50">
            <i className="ri-list-check-3 text-red-600 text-lg"></i>
          </div>
          <div>
            <p className="text-xs text-gray-400">积分科目总数</p>
            <p className="text-xl font-bold text-gray-800">{categories.length}</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-green-50">
            <i className="ri-check-double-line text-green-600 text-lg"></i>
          </div>
          <div>
            <p className="text-xs text-gray-400">启用中科目</p>
            <p className="text-xl font-bold text-gray-800">{activeCount}</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-50">
            <i className="ri-star-line text-amber-600 text-lg"></i>
          </div>
          <div>
            <p className="text-xs text-gray-400">启用类别积分值合计</p>
            <p className="text-xl font-bold text-gray-800">{totalPoints}</p>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="搜索积分科目..."
            className="pl-8 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-red-400 w-56"
          />
        </div>
        <div className="flex items-center gap-2">
          {savedFlash && (
            <span className="text-xs text-green-600 flex items-center gap-1">
              <i className="ri-checkbox-circle-fill"></i> 已保存
            </span>
          )}
          <button
            onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm rounded-xl hover:bg-red-700 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i> 添加积分科目
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5">科目名称</th>
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5">科目类型</th>
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5">计算单位</th>
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5">积分值（每单位）</th>
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5">说明</th>
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5">创建日期</th>
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5">状态</th>
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5">操作</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-16 text-gray-400 text-sm">
                  <i className="ri-inbox-line text-3xl block mb-2"></i>
                  暂无积分科目
                </td>
              </tr>
            ) : (
              filtered.map((cat) => (
                <tr key={cat.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 flex-shrink-0">
                        <i className="ri-award-line text-red-500 text-sm"></i>
                      </div>
                      <span className="text-sm font-medium text-gray-800">{cat.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-lg ${(cat.subjectType ?? "award") === "deduct" ? "bg-red-50 text-red-600" : "bg-blue-50 text-blue-600"}`}>
                      {(cat.subjectType ?? "award") === "deduct" ? "扣除" : "奖励"}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-sm text-gray-600 bg-gray-100 px-2.5 py-1 rounded-lg">{cat.unit}</span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-star-fill text-amber-400 text-sm"></i>
                      <span className="text-sm font-semibold text-gray-800">{cat.pointsPerUnit}</span>
                      <span className="text-xs text-gray-400">积分 / {cat.unit}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-sm text-gray-500 max-w-xs block truncate">{cat.description || "—"}</span>
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-sm text-gray-400">{cat.createdAt}</span>
                  </td>
                  <td className="px-5 py-4">
                    <button
                      onClick={() => toggleActive(cat.id)}
                      className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${cat.isActive ? "bg-green-500" : "bg-gray-300"}`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${cat.isActive ? "left-4" : "left-0.5"}`}></span>
                    </button>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEdit(cat)}
                        className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors"
                        title="编辑"
                      >
                        <i className="ri-edit-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => setDeleteConfirmId(cat.id)}
                        className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg cursor-pointer transition-colors"
                        title="删除"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Tip */}
      <div className="mt-4 flex items-start gap-2 text-xs text-gray-400 bg-amber-50 rounded-xl px-4 py-3">
        <i className="ri-information-line text-amber-500 mt-0.5 flex-shrink-0"></i>
        <span>积分科目字典用于积分管理与活动额外积分；科目分为「奖励」与「扣除」两种类型。</span>
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-800">{editingId ? "编辑积分科目" : "添加积分科目"}</h3>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Subject type */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  科目类型 <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.subjectType}
                  onChange={(e) => setForm((p) => ({ ...p, subjectType: e.target.value as PointsSubjectType }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 cursor-pointer"
                >
                  <option value="award">奖励</option>
                  <option value="deduct">扣除</option>
                </select>
              </div>

              {/* Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  科目名称 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="如：活动志愿服务"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                  maxLength={30}
                />
              </div>

              {/* Unit */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  计算单位 <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <select
                    value={form.unit}
                    onChange={(e) => setForm((p) => ({ ...p, unit: e.target.value }))}
                    className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 cursor-pointer"
                  >
                    {unitOptions.map((u) => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                    <option value="custom">自定义...</option>
                  </select>
                  {form.unit === "custom" && (
                    <input
                      type="text"
                      value={form.customUnit}
                      onChange={(e) => setForm((p) => ({ ...p, customUnit: e.target.value }))}
                      placeholder="输入单位名称"
                      className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                      maxLength={10}
                    />
                  )}
                </div>
              </div>

              {/* Points per unit */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  积分值（每单位获得）<span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={form.pointsPerUnit}
                    onChange={(e) => setForm((p) => ({ ...p, pointsPerUnit: e.target.value === "" ? "" : Number(e.target.value) }))}
                    placeholder="如：10"
                    min={1}
                    max={9999}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 pr-16"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                    积分 / {form.unit === "custom" ? (form.customUnit || "单位") : form.unit}
                  </span>
                </div>
                {form.pointsPerUnit !== "" && Number(form.pointsPerUnit) > 0 && (
                  <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                    <i className="ri-star-fill"></i>
                    每登记 1 {form.unit === "custom" ? (form.customUnit || "单位") : form.unit}，会员获得 {form.pointsPerUnit} 积分
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">说明（选填）</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  rows={2}
                  placeholder="对此积分科目的简要说明..."
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 resize-none"
                  maxLength={100}
                ></textarea>
              </div>

              {/* Active toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div>
                  <p className="text-sm font-medium text-gray-700">启用此科目</p>
                  <p className="text-xs text-gray-400 mt-0.5">停用后将不出现在积分管理选项中</p>
                </div>
                <button
                  onClick={() => setForm((p) => ({ ...p, isActive: !p.isActive }))}
                  className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${form.isActive ? "bg-green-500" : "bg-gray-300"}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${form.isActive ? "left-5" : "left-0.5"}`}></span>
                </button>
              </div>
            </div>

            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                disabled={!isFormValid}
                className={`flex-1 py-2.5 text-sm rounded-xl cursor-pointer whitespace-nowrap transition-colors ${
                  isFormValid ? "bg-red-600 text-white hover:bg-red-700" : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
                {editingId ? "保存修改" : "确认添加"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl p-6 text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-50 mx-auto mb-3">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="font-bold text-gray-800 mb-1">确认删除</h3>
            <p className="text-sm text-gray-500 mb-5">
              删除后相关积分科目将无法恢复，已记录的历史数据不受影响。
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmId)}
                className="flex-1 py-2.5 text-sm bg-red-600 text-white rounded-xl hover:bg-red-700 cursor-pointer whitespace-nowrap"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
